import { supabase } from "@/integrations/supabase/client";

export interface SendTaskEmailParams {
  type: "assignment" | "creator_notification" | "due_date_reminder" | "admin_task_notification";
  toEmail: string;
  toName: string;
  taskTitle: string;
  taskDescription?: string;
  category: string;
  priorityLabel?: string;
  dueDate?: string;
  taskId: string;
  creatorName?: string;
  assigneeNames?: string[];
  status?: string;
  userId?: string;
  unaId?: string;
}

export async function sendTaskEmail(params: SendTaskEmailParams) {
  try {
    console.log(`Sending ${params.type} email to ${params.toEmail}`);

    const { data, error } = await supabase.functions.invoke("send-task-email", {
      body: params,
    });

    if (error) {
      console.error("Error sending email:", error);
      throw error;
    }

    console.log("Email sent successfully:", data);
    return { success: true, data };
  } catch (error: any) {
    console.error("Failed to send email:", error);
    return { success: false, error: error.message };
  }
}

export interface AdminTaskEmailParams {
  recipientEmail: string;
  recipientName: string;
  taskTitle: string;
  taskDescription?: string | null;
  dueDate?: string | null;
  category: string;
  taskId: string;
}

export async function sendAdminTaskAssignedEmail(params: AdminTaskEmailParams): Promise<boolean> {
  const { recipientEmail, recipientName, taskTitle, taskDescription, dueDate, category, taskId } = params;

  const formattedDueDate = dueDate
    ? new Date(dueDate).toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : undefined;

  const categoryDisplay = getCategoryLabel(category).replace(/^[^\s]+\s/, ""); // Remove emoji

  try {
    const result = await sendTaskEmail({
      type: "admin_task_notification",
      toEmail: recipientEmail,
      toName: recipientName,
      taskTitle,
      taskDescription: taskDescription || undefined,
      category: categoryDisplay,
      dueDate: formattedDueDate,
      taskId,
    });

    return result.success;
  } catch (error) {
    console.error("Failed to send admin task email:", error);
    return false;
  }
}

export function getQuadrantLabel(quadrant: string): string {
  const labels: { [key: string]: string } = {
    q1_do_first: "🔴 Q1: Do First (Important & Urgent)",
    q2_schedule: "🔵 Q2: Schedule (Important, Not Urgent)",
    q3_delegate: "🟡 Q3: Delegate (Not Important, Urgent)",
    q4_eliminate: "⚪ Q4: Eliminate (Not Important, Not Urgent)",
  };
  return labels[quadrant] || "🔵 Q2: Schedule";
}

export function getCategoryLabel(category: string): string {
  const labels: { [key: string]: string } = {
    member: "🔵 Member-Related",
    asset: "🟢 Asset-Related",
    contribution: "🟡 Contribution-Related",
    resolution: "🟠 Resolution-Related",
    documentation: "🔴 Documentation-Related",
    banking: "🟣 Banking & Finance",
    business: "⚫ Business-Related",
    other: "⚪ Other",
  };
  return labels[category] || "⚪ Other";
}

export function formatEmailDate(date: string | Date): string {
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function getStatusLabel(status: string): string {
  const labels: { [key: string]: string } = {
    not_started: "📋 Not Started",
    in_progress: "🏃 In Progress",
    blocked: "⛔ Blocked",
    completed: "✅ Completed",
  };
  return labels[status] || "📋 Not Started";
}
